import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  Fade,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useForm } from "react-hook-form";
import { TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ConfirmDialog from "components/common/ConfirmDialog";
import usePageGuide from "./usePageGuide";
import PageGuideTalkBack from "./PageGuideTalkBack";
import {
  acquireVoiceSession,
  releaseVoiceSession,
} from "./voiceSessionRegistry";
import { speakPageGuideText, unlockPageGuideAudio } from "./pageGuideAudio";
import { invokeLLMPrompt } from "services/llmService";

/**
 * Generic PageGuide mount — behavior comes entirely from Firestore config by pageId.
 */
function PageGuide({ pageId, facilityId = null }) {
  const guide = usePageGuide({ pageId, facilityId });
  const [talkBackSession, setTalkBackSession] = useState(0);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({ defaultValues: { question: "" } });

  const onTextFallback = handleSubmit(async ({ question }) => {
    const transcript = String(question || "").trim();
    if (!transcript) return;
    guide.setCaption("Let me check…");
    unlockPageGuideAudio();
    await speakPageGuideText("Let me check…");
    try {
      if (!acquireVoiceSession(guide.PAGE_GUIDE_SESSION_ID)) {
        guide.setTalkBackError("Another voice feature is active.");
        return;
      }
      const result = await invokeLLMPrompt({
        promptId: "page-guide-assistant",
        userMessage: JSON.stringify({
          pageContext: guide.pageContext,
          stepId: guide.activeStep?.id || null,
          nudgeText: guide.activeStep?.nudgeText || null,
          transcript,
        }),
      });
      const reply =
        typeof result?.output === "string"
          ? result.output
          : typeof result?.content === "string"
            ? result.content
            : "";
      const spoken = reply.trim() || "Sorry — I could not answer that just now.";
      guide.setCaption(spoken);
      await speakPageGuideText(spoken);
      reset({ question: "" });
      guide.setTextFallbackOpen(false);
    } catch (err) {
      const msg = err?.message || "Something went wrong. Try again.";
      guide.setTalkBackError(msg);
      guide.setCaption(msg);
    } finally {
      releaseVoiceSession(guide.PAGE_GUIDE_SESSION_ID);
    }
  });

  const beginTalkBackAfterPrime = useCallback(() => {
    guide.setMicPrimingOpen(false);
    unlockPageGuideAudio();
    guide.setTalkBackActive(true);
    setTalkBackSession((n) => n + 1);
  }, [guide]);

  if (guide.loading) {
    return null;
  }

  const showIntroChrome =
    Boolean(guide.caption || guide.soundBlocked) && !guide.masterOk;
  const showCoachingChrome = guide.masterOk;

  if (!showIntroChrome && !showCoachingChrome) {
    return null;
  }

  const targetEl =
    typeof document !== "undefined" && guide.activeStep?.selector
      ? document.querySelector(guide.activeStep.selector)
      : null;

  return (
    <>
      {/* Caption near target or bottom-safe fallback */}
      <Fade
        in={Boolean(
          guide.caption || guide.soundBlocked || guide.talkBackError,
        )}
      >
        <Paper
          variant="outlined"
          data-testid="pageguide-caption"
          sx={{
            position: "fixed",
            zIndex: 1200,
            bottom: { xs: 88, sm: 96 },
            left: "50%",
            transform: "translateX(-50%)",
            maxWidth: 420,
            width: "calc(100% - 32px)",
            p: 1.5,
            bgcolor: "background.paper",
            borderColor: "divider",
          }}
        >
          <Stack spacing={1}>
            {guide.iosHeadphoneWarning ? (
              <Typography variant="caption" color="text.secondary">
                On iPhone/iPad, starting the mic may switch audio from headphones
                to the speaker.
              </Typography>
            ) : null}
            {guide.caption ? (
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ whiteSpace: "pre-line" }}
              >
                {guide.caption}
              </Typography>
            ) : null}
            {guide.talkBackError ? (
              <Typography variant="body2" color="text.secondary">
                {guide.talkBackError}
              </Typography>
            ) : null}
            {guide.soundBlocked ? (
              <Chip
                icon={<VolumeUpIcon />}
                label="Tap to enable sound"
                onClick={guide.enableSound}
                clickable
                color="default"
                variant="outlined"
                data-testid="pageguide-enable-sound"
              />
            ) : null}
          </Stack>
        </Paper>
      </Fade>

      {showCoachingChrome ? (
        <>
          {/* Persistent help / talk-back / dismiss cluster */}
          <Box
            data-testid="pageguide-affordance"
            sx={{
              position: "fixed",
              zIndex: 1200,
              bottom: { xs: 24, sm: 32 },
              right: { xs: 16, sm: 24 },
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Stop guiding me on this page">
                <IconButton
                  type="button"
                  aria-label="Stop guiding me on this page"
                  onClick={guide.dismissForPage}
                  size="small"
                  data-testid="pageguide-dismiss"
                  sx={{ color: "text.secondary" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Talk to guide">
                <IconButton
                  type="button"
                  aria-label="Talk to guide"
                  color="primary"
                  onClick={guide.requestTalkBack}
                  data-testid="pageguide-talkback"
                  sx={{
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  {guide.helpAffordance ? <HelpOutlineIcon /> : <MicIcon />}
                </IconButton>
              </Tooltip>
            </Stack>
            {targetEl ? null : (
              <Typography variant="caption" color="text.secondary">
                Guide ready
              </Typography>
            )}
          </Box>

          <ConfirmDialog
            open={guide.micPrimingOpen}
            title="Microphone access"
            message="PageGuide uses your microphone only while you talk to the guide — never in the background. Continue to allow mic access in the next browser prompt."
            confirmLabel="Continue"
            confirmColor="primary"
            onConfirm={beginTalkBackAfterPrime}
            onClose={() => guide.setMicPrimingOpen(false)}
            loading={false}
          />

          {guide.talkBackActive ? (
            <PageGuideTalkBack
              key={talkBackSession}
              pageContext={guide.pageContext}
              activeStep={guide.activeStep}
              sessionId={guide.PAGE_GUIDE_SESSION_ID}
              onCaption={guide.setCaption}
              onError={(msg) => {
                guide.setTalkBackError(msg || "");
                if (msg) guide.setCaption(msg);
              }}
              onEnded={() => guide.setTalkBackActive(false)}
              preferTextFallback={guide.isStandalonePwa}
              onNeedTextFallback={() => {
                guide.setTalkBackActive(false);
                guide.setTextFallbackOpen(true);
              }}
            />
          ) : null}

          {guide.textFallbackOpen ? (
            <Paper
              variant="outlined"
              data-testid="pageguide-text-fallback"
              sx={{
                position: "fixed",
                zIndex: 1210,
                bottom: 160,
                left: "50%",
                transform: "translateX(-50%)",
                width: "calc(100% - 32px)",
                maxWidth: 420,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Type your question
              </Typography>
              <Stack
                component="form"
                spacing={1.5}
                onSubmit={onTextFallback}
                noValidate
              >
                <Controller
                  name="question"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Ask the guide"
                      fullWidth
                      multiline
                      minRows={2}
                      autoFocus
                    />
                  )}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <IconButton
                    type="button"
                    aria-label="Close"
                    onClick={() => guide.setTextFallbackOpen(false)}
                  >
                    <CloseIcon />
                  </IconButton>
                  <LoadingButton type="submit" loading={isSubmitting}>
                    Ask
                  </LoadingButton>
                </Stack>
              </Stack>
            </Paper>
          ) : null}
        </>
      ) : null}
    </>
  );
}

PageGuide.propTypes = {
  pageId: PropTypes.string.isRequired,
  facilityId: PropTypes.string,
};

export default PageGuide;
